/**
 * <Modal /> — lightweight modal shell primitive for ALGOS.
 *
 * What this provides (and only this):
 *   • Mount-gated rendering — children only render while `open === true`,
 *     so any state inside them starts fresh on each open (no
 *     reset-from-effect needed).
 *   • Backdrop click → close.
 *   • Esc key → close.
 *   • Accessible dialog wrapper (role="dialog" + aria-modal + aria-labelledby).
 *   • Title / optional eyebrow / optional description.
 *   • Optional footer slot.
 *
 * What it deliberately does NOT do (keep it small):
 *   • No focus-trap library — the underlying browser dialog semantics
 *     are enough for our enterprise screens. Add later if needed.
 *   • No portal — we render in place, matching the existing modals.
 *   • No animation orchestration — caller adds `anim-fade-up` etc.
 *
 * Usage:
 *   <Modal
 *     open={open}
 *     onClose={onClose}
 *     eyebrow="Identify"
 *     title="Submit AI opportunity"
 *     description="Step 1 of 4 — Opportunity"
 *     widthClass="max-w-[720px]"
 *     testId="submit-opp-modal"
 *     footer={<FooterActions />}
 *   >
 *     <FormBody />
 *   </Modal>
 */
import React, { useEffect, useId } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export function Modal({
  open,
  onClose,
  eyebrow,
  title,
  description,
  children,
  footer,
  widthClass = "max-w-[640px]",
  testId,
  zIndexClass = "z-[60]",
  bodyPadded = true,
}) {
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={cn("fixed inset-0", zIndexClass)}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
      data-testid={testId}
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid={testId ? `${testId}-backdrop` : "modal-backdrop"}
      />
      <div className="relative h-full w-full flex items-start justify-center overflow-y-auto py-6 px-4">
        <div
          className={cn(
            "w-full bg-raised border border-border-subtle rounded-lg shadow-e4 overflow-hidden anim-fade-up",
            widthClass,
          )}
        >
          {(title || eyebrow) && (
            <ModalHeader
              eyebrow={eyebrow}
              title={title}
              description={description}
              titleId={titleId}
              descId={descId}
              onClose={onClose}
              testId={testId}
            />
          )}
          {bodyPadded ? <div className="px-6 py-5">{children}</div> : children}
          {footer && (
            <div
              className="px-6 py-3 border-t border-border-subtle bg-inset/30 flex items-center gap-2"
              data-testid={testId ? `${testId}-footer` : undefined}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalHeader({ eyebrow, title, description, titleId, descId, onClose, testId }) {
  return (
    <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-border-subtle">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        {title && (
          <h2 id={titleId} className="text-h2 text-ink-primary mt-0.5">
            {title}
          </h2>
        )}
        {description && (
          <p id={descId} className="text-meta text-ink-secondary mt-1">
            {description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset"
        data-testid={testId ? `${testId}-close` : "modal-close"}
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default Modal;
